import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tooltip,
    CircularProgress,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    useTheme,
} from "@mui/material";
import Axios from "axios";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import PrepTestReportModal from "./PrepTestReportModal";
import StudentEttInfoModal from "./StudentEttInfoModal";
import { StudentHeaderContent } from "./prepStudentHeader";
import { Pencil } from "lucide-react";
import ExamHistoryModal from "./ExamHistoryModal";
import { renderV2Cell, V2_UNLINKED_GLYPH } from "./prepV2Cell";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
// A DATEONLY ("YYYY-MM-DD") shown as "Jul 30" — parsed as local so there's no UTC day-shift.
const fmtDay = (d) => {
    if (!d) return "";
    const [y, m, day] = String(d).slice(0, 10).split("-").map(Number);
    return y ? new Date(y, m - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : String(d);
};

// Rows come from two sources and are merged chronologically server-side: ETT practice exams
// (kind "ett") and any library test flagged "In Practice Exams" (kind "v2"). row_key namespaces
// the two id spaces; the fallback covers a client-ahead-of-server deploy.
const rowKey = (row) => row.row_key || String(row.id);
const isV2 = (row) => row.kind === "v2";

// Exams (rows) × students (columns). Each cell shows the best score if taken, otherwise a
// status (in progress / not started / not assigned / not linked).
const PrepExamMatrix = ({ uuid }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [reportModal, setReportModal] = useState(null); // { ettUserId, testId, examTitle, studentName }
    const [infoStudent, setInfoStudent] = useState(null); // student whose ETT-email modal is open
    const [historyModal, setHistoryModal] = useState(null); // exam whose score-history modal is open
    const [pdfUrl, setPdfUrl] = useState(null); // uploaded history PDF being viewed

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const res = await Axios.get(`${BASE}/${uuid}/exam-matrix`, {
                headers: { Authorization: getAuthHeader() },
            });
            setData(res.data);
        } catch (e) {
            console.error("Exam matrix load error:", e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [uuid]);

    useEffect(() => {
        load();
    }, [load]);

    const renderCell = (cell, exam, student) => {
        if (!cell) return <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>;
        if (isV2(exam)) return renderV2Cell(cell, exam);
        switch (cell.status) {
            case "taken": {
                const late = cell.on_time === false;
                const incomplete = !!cell.incomplete; // finished but ungraded / partial score
                const ettUserId = student?.linkage?.ett_user_id;
                const clickable = !!ettUserId; // need the ETT user to fetch the report
                const tip = [
                    incomplete ? "finished — score pending/partial" : (cell.detail || ""),
                    cell.finished_at ? fmtDate(cell.finished_at) : null,
                    late ? "late" : null,
                    cell.attempts > 1 ? `${cell.attempts} attempts` : null,
                    clickable ? "click for report" : null,
                ].filter(Boolean).join(" · ");
                return (
                    <Tooltip title={tip} arrow>
                        <Typography
                            variant="body2"
                            onClick={clickable ? () => setReportModal({
                                ettUserId, testId: exam.ett_exam_id,
                                examTitle: exam.exam_title, studentName: student.name || student.email,
                            }) : undefined}
                            sx={{
                                color: incomplete ? "info.main" : late ? "warning.main" : "success.main",
                                whiteSpace: "nowrap",
                                cursor: clickable ? "pointer" : "default",
                                textDecoration: clickable ? "underline dotted" : "none",
                                "&:hover": clickable ? { textDecoration: "underline" } : {},
                            }}
                        >
                            {incomplete ? (
                                <Box component="span" sx={{ fontWeight: 700 }}>✓ pending</Box>
                            ) : (
                                <>
                                    <Box component="span" sx={{ fontWeight: 700 }}>{cell.score_total}</Box>
                                    {cell.detail ? ` (${cell.detail})` : ""}{late ? " *" : ""}
                                </>
                            )}
                        </Typography>
                    </Tooltip>
                );
            }
            case "in_progress":
                return <Chip size="small" color="info" variant="outlined" label="In progress" />;
            case "not_started":
                return <Typography variant="caption" sx={{ color: "text.disabled" }}>Not started</Typography>;
            case "not_assigned":
                return <Tooltip title="Not assigned to this student" arrow><Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography></Tooltip>;
            case "no_link":
                return <Tooltip title="Student not linked to ETT" arrow><Typography variant="caption" sx={{ color: "warning.main" }}>?</Typography></Tooltip>;
            case "ett_error":
                return <Tooltip title="Couldn't load this student's results from ETT (temporary) — refresh to retry" arrow><Typography variant="caption" sx={{ color: "error.main" }}>⚠</Typography></Tooltip>;
            default:
                return <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>;
        }
    };

    // Preserved manual history under the live score, newest date first (the server orders on
    // taken_on, which is the field shown here). PDF entries open the PDF; the rest are plain text.
    const renderHistory = (cell) => {
        const rows = cell?.history || [];
        if (!rows.length) return null;
        return (
            <Box sx={{ mt: 0.25 }}>
                {rows.map((h) => (
                    <Typography
                        key={h.id}
                        variant="caption"
                        component="div"
                        onClick={h.pdf_url ? () => setPdfUrl(h.pdf_url) : undefined}
                        sx={{
                            color: h.pdf_url ? "info.main" : "text.primary",
                            cursor: h.pdf_url ? "pointer" : "default",
                            textDecoration: h.pdf_url ? "underline dotted" : "none",
                            whiteSpace: "nowrap",
                            lineHeight: 1.35,
                            "&:hover": h.pdf_url ? { textDecoration: "underline" } : {},
                        }}
                    >
                        {fmtDay(h.taken_on)} · <Box component="span" sx={{ fontWeight: 700 }}>{h.score_total}</Box>{h.detail ? ` (${h.detail})` : ""}
                    </Typography>
                ))}
            </Box>
        );
    };

    // Save the uploaded PDF locally: fetch the S3 object (public-read) → download; if a
    // cross-origin fetch is blocked, fall back to opening it in a new tab.
    const downloadPdf = async () => {
        if (!pdfUrl) return;
        let filename = (pdfUrl.split("/").pop() || "score.pdf").split("?")[0];
        try { filename = decodeURIComponent(filename); } catch (e) { /* keep encoded name */ }
        try {
            const resp = await fetch(pdfUrl);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            window.open(pdfUrl, "_blank", "noopener");
        }
    };

    if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    if (error) return <Typography color="error" sx={{ py: 2 }}>Could not load the exam matrix.</Typography>;

    const exams = data?.exams || [];
    const students = data?.students || [];
    // Students who actually sit the ETT practice exams. Older servers omit sits_exams, in which case
    // every column was a PRACTICE enrollee anyway.
    const examStudents = students.filter((s) => s.sits_exams !== false);

    if (!exams.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No practice exams have been added to this program.</Typography>;
    if (!students.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No students are enrolled in this program yet.</Typography>;

    const stickyHeadCell = {
        position: "sticky",
        left: 0,
        zIndex: 3,
        backgroundColor: colors.primary[600],
        width: 240,
        maxWidth: 240,
    };
    const stickyBodyCell = {
        position: "sticky",
        left: 0,
        zIndex: 1,
        backgroundColor: colors.primary[600],
        width: 240,
        maxWidth: 240,
    };

    // Per-row taken count, over the students the row actually reaches — a SELECTED-mode exam, or a
    // library test scoped to one course, doesn't reach every column.
    const takenCount = (exam) =>
        students.filter((s) => data.cells?.[s.student_id]?.[rowKey(exam)]?.status === "taken").length;
    const assignedCount = (exam) =>
        students.filter((s) => {
            const st = data.cells?.[s.student_id]?.[rowKey(exam)]?.status;
            return st && st !== "not_assigned";
        }).length;

    return (
        <Box>
            <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600], maxHeight: "70vh", width: "fit-content", maxWidth: "100%" }}>
                <Table
                    stickyHeader
                    size="small"
                    sx={{
                        width: "auto",
                        // Full grid: separate each exam (row) and each student score (column).
                        "& .MuiTableCell-root": {
                            borderRight: `1px solid ${theme.palette.divider}`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        "& .MuiTableCell-root:last-child": { borderRight: "none" },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...stickyHeadCell, zIndex: 4 }}>Exam</TableCell>
                            {students.map((s) => (
                                <TableCell key={s.student_id} align="center" sx={{ minWidth: 130, fontWeight: 600 }}>
                                    <StudentHeaderContent student={s} onInfo={setInfoStudent} />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {exams.map((exam) => (
                            <TableRow key={rowKey(exam)} hover>
                                <TableCell sx={{ ...stickyBodyCell, pr: 4.5 }}>
                                    {/* component="div": a Chip is a <div> and body2 maps to <p>. */}
                                    <Typography variant="body2" component="div" fontWeight={600}>
                                        {exam.exam_title}
                                        {isV2(exam) && (
                                            <Chip size="small" label="Library" variant="outlined" sx={{ ml: 0.75, height: 17, fontSize: 10 }} />
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {/* A library row is scoped to ONE course, so most columns can read "—"
                                            legitimately; name the course or that looks like a broken row. */}
                                        {isV2(exam) && exam.course?.label ? `${exam.course.label} · ` : ""}
                                        {exam.due_date ? `Due ${fmtDate(exam.due_date)} · ` : ""}
                                        {assignedCount(exam) === 0
                                            ? "assigned to nobody"
                                            : `${takenCount(exam)}/${assignedCount(exam)} taken`}
                                        {/* Explains scores that predate the program, and whether they count. */}
                                        {exam.adopted ? ` · adopted${exam.include_prior_attempts ? "" : ", prior attempts ignored"}` : ""}
                                    </Typography>
                                    {/* Manual score history is ETT-only — it's keyed to prep_program_exams. */}
                                    {!isV2(exam) && (
                                        <Tooltip title="Add / edit score history" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => setHistoryModal(exam)}
                                                sx={{ position: "absolute", top: 3, right: 3, color: "text.secondary" }}
                                            >
                                                <Pencil size={14} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                {students.map((s) => {
                                    const cell = data.cells?.[s.student_id]?.[rowKey(exam)];
                                    return (
                                        <TableCell key={s.student_id} align="center">
                                            {renderCell(cell, exam, s)}
                                            {renderHistory(cell)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                    <b style={{ color: theme.palette.success.main }}>The bold score</b> = total (SAT) / composite (ACT) — hover a score for the section breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    <b style={{ color: theme.palette.warning.main }}>*</b> = completed after the due date
                </Typography>
                <Typography variant="caption" color="text.secondary">In progress · Not started · — not assigned · ? not linked · ⚠ load error (refresh)</Typography>
                {exams.some(isV2) && (
                    <Typography variant="caption" color="text.secondary">
                        <b>Library</b> = a Test-Library test surfaced here — click a score for its result page (no section
                        breakdown, no manual history) · <b>{V2_UNLINKED_GLYPH}</b> = its adopted assignment was deleted
                        (remove the test from the program)
                    </Typography>
                )}
            </Box>

            <PrepTestReportModal
                open={!!reportModal}
                onClose={() => setReportModal(null)}
                ettUserId={reportModal?.ettUserId}
                testId={reportModal?.testId}
                examTitle={reportModal?.examTitle}
                studentName={reportModal?.studentName}
            />

            <StudentEttInfoModal
                open={!!infoStudent}
                onClose={() => setInfoStudent(null)}
                student={infoStudent}
            />

            {/* Manual history is an ETT-exam concept, so it's offered only for the exam-sitting
                cohort. Columns that exist purely because a flagged library test reaches them would
                otherwise be selectable, and a score saved against one renders a bold number under a
                cell reading "not assigned". */}
            <ExamHistoryModal
                open={!!historyModal}
                onClose={() => setHistoryModal(null)}
                programUuid={uuid}
                exam={historyModal}
                students={examStudents}
                qbType={data?.qb_type}
                cells={data?.cells}
                onSaved={load}
            />

            <Dialog
                open={!!pdfUrl}
                onClose={() => setPdfUrl(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: "88vh" } }}
            >
                <DialogContent sx={{ p: 0, flex: 1, display: "flex" }}>
                    {pdfUrl && (
                        <iframe title="Score PDF" src={pdfUrl} style={{ flex: 1, width: "100%", border: "none" }} />
                    )}
                </DialogContent>
                <DialogActions sx={{ flexShrink: 0 }}>
                    <Button onClick={downloadPdf}>Save PDF</Button>
                    <Button onClick={() => setPdfUrl(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PrepExamMatrix;
